'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';

// ─────────────────────────────────────────────────────────────────────────────
// DATA: 8 BIOME RINGS — each mapped to a domain from Quran 2:164
// "Indeed, in creation the heavens and the earth, and alternation of the night
//  and the day, and the ships which sail in the sea... surely Signs for a people
//  who use their intellect." — The verse IS the mural's source code.
// ─────────────────────────────────────────────────────────────────────────────

const BIOME_RINGS = [
  {
    ring: 0,
    biome: 'Forest',
    arabic: 'الغابة',
    domain2164: 'Creation of the Earth',
    domain2164Ar: 'خَلْقِ الْأَرْضِ',
    verse2164Fragment: 'creation of the heavens and the earth',
    color: '#2d7a47',
    praiseMode: 'Creation',
    names: ['Al-Khaliq', 'Al-Bari', 'Al-Musawwir'],
    cities: ['Atlanta', 'Durham'],
    irlLayers: ['Canopy = Zahir (Layer 1)', 'Understory = Three Questions (Layer 2)', 'Forest Floor = Names Extraction (Layer 3)', 'Root Zone = Sunnat Allah (Layer 4)', 'Mycorrhizal = Zahir-Batin (Layer 5)', 'Soil = Haqq (Layer 6)', 'Bedrock = Amanah (Layer 7)', 'Groundwater = Uboor (Layer 8)'],
    hikmahThreshold: 'Forest → Savannah: Where shade meets open sky',
    resourceFlow: 'Photosynthesis → Oxygen → Provision → Life',
    sequences: ['Canopy Photosynthesis', 'Mycorrhizal Network', 'Amphibian Metamorphosis'],
  },
  {
    ring: 1,
    biome: 'Savannah Transition',
    arabic: 'السافانا',
    domain2164: 'Alternation of Night and Day',
    domain2164Ar: 'اخْتِلَافِ اللَّيْلِ وَالنَّهَارِ',
    verse2164Fragment: 'alternation of the night and the day',
    color: '#c9a84c',
    praiseMode: 'Rhythm',
    names: ['Al-Hayy', 'Al-Qayyum', 'Al-Awwal'],
    cities: ['Atlanta', 'Durham'],
    irlLayers: ['Dawn light = Fajr encounter', 'Dusk = Maghrib recognition'],
    hikmahThreshold: 'Savannah → Grasslands: Where trees thin and sky opens',
    resourceFlow: 'Light cycles → Growth rhythms → Seasonal provision',
    sequences: ['Longleaf Pine Grass Stage'],
  },
  {
    ring: 2,
    biome: 'Grasslands',
    arabic: 'الأراضي العشبية',
    domain2164: 'Ships Sailing the Sea',
    domain2164Ar: 'الْفُلْكِ الَّتِي تَجْرِي فِي الْبَحْرِ',
    verse2164Fragment: 'ships which sail in the sea with what benefits the people',
    color: '#8a8ab8',
    praiseMode: 'Patience',
    names: ['As-Sabur', 'Al-Hayy', 'Ar-Razzaq'],
    cities: ['Chicago', 'Omaha'],
    irlLayers: ['Resilience = endurance through fire and drought', 'Return = grass that grows back'],
    hikmahThreshold: 'Grasslands → Wetlands: Where dry meets saturated',
    resourceFlow: 'Root networks → Soil carbon → Aquifer recharge',
    sequences: ['Big Bluestem'],
  },
  {
    ring: 3,
    biome: 'Wetlands',
    arabic: 'الأراضي الرطبة',
    domain2164: 'Rain Sent Down from the Sky',
    domain2164Ar: 'مَا أَنزَلَ اللَّهُ مِنَ السَّمَاءِ مِن مَّاءٍ',
    verse2164Fragment: 'what Allah sent down from the sky of water, giving life to the earth after its death',
    color: '#2d8a87',
    praiseMode: 'Purification',
    names: ['Al-Latif', 'Al-Khabir', 'Al-Muhyi'],
    cities: ['Detroit', 'New Orleans'],
    irlLayers: ['Filtration = subtle purification', 'Absorption = hidden reception'],
    hikmahThreshold: 'Wetlands → River Systems: Where still meets flowing',
    resourceFlow: 'Filtration → Clean water → Downstream provision',
    sequences: ['Wetland Filtration'],
  },
  {
    ring: 4,
    biome: 'River Systems',
    arabic: 'الأنهار',
    domain2164: 'Every Moving Creature',
    domain2164Ar: 'كُلِّ دَابَّةٍ',
    verse2164Fragment: 'dispersing therein of every moving creature',
    color: '#4a8ab8',
    praiseMode: 'Flow',
    names: ['Al-Muhyi', 'Ar-Razzaq', 'Al-Wadud'],
    cities: ['Portland', 'Seattle'],
    irlLayers: ['Current = direction of provision', 'Confluence = where paths meet'],
    hikmahThreshold: 'Rivers → Coastal: Where fresh meets salt',
    resourceFlow: 'Upstream nutrients → Salmon run → Forest fertilization',
    sequences: ['Salmon Run'],
  },
  {
    ring: 5,
    biome: 'Coastal',
    arabic: 'السواحل',
    domain2164: 'Directing the Winds',
    domain2164Ar: 'تَصْرِيفِ الرِّيَاحِ',
    verse2164Fragment: 'directing the winds',
    color: '#3a7a9a',
    praiseMode: 'Boundary',
    names: ['Al-Qahhar', 'Al-Aziz', 'Al-Hafiz'],
    cities: ['New Orleans', 'Portland'],
    irlLayers: ['Tidal rhythm = Barzakh between land and sea', 'Estuary = dissolved boundary'],
    hikmahThreshold: 'Coastal → Desert: Where moisture ends and aridity begins',
    resourceFlow: 'Tidal exchange → Estuarine nurseries → Ocean provision',
    sequences: [],
  },
  {
    ring: 6,
    biome: 'Desert',
    arabic: 'الصحراء',
    domain2164: 'Clouds Controlled Between Sky and Earth',
    domain2164Ar: 'السَّحَابِ الْمُسَخَّرِ بَيْنَ السَّمَاءِ وَالْأَرْضِ',
    verse2164Fragment: 'clouds controlled between the sky and the earth',
    color: '#c9a84c',
    praiseMode: 'Endurance',
    names: ['As-Sabur', 'Al-Ghani', 'Al-Qadir'],
    cities: ['Phoenix', 'Albuquerque'],
    irlLayers: ['Scarcity = recognition of provision', 'Patience = endurance as praise'],
    hikmahThreshold: 'Desert → Quantum Threshold: Where visible meets invisible',
    resourceFlow: 'Sparse rain → Flash floods → Desert bloom',
    sequences: [],
  },
  {
    ring: 7,
    biome: 'Quantum Threshold',
    arabic: 'العتبة الكمية',
    domain2164: 'Signs for People Who Use Their Intellect',
    domain2164Ar: 'لِّقَوْمٍ يَعْقِلُونَ',
    verse2164Fragment: 'surely Signs for a people who use their intellect',
    color: '#8a8ab8',
    praiseMode: 'Return',
    names: ['Al-Awwal', 'Al-Akhir', 'Al-Zahir', 'Al-Batin'],
    cities: ['All Cities'],
    irlLayers: ['Ghayb = the unseen as destination', 'Uboor = the crossing-over'],
    hikmahThreshold: 'Threshold → Ghayb: The return — "to Him you will be returned" (36:83)',
    resourceFlow: 'Recognition → Amanah → Return to Source',
    sequences: [],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// FULL INTERACTIVE MURAL — Forest Watershed Descent
// ─────────────────────────────────────────────────────────────────────────────

export function FullInteractiveMural() {
  const [activeRing, setActiveRing] = useState<number | null>(null);
  const [showIqra, setShowIqra] = useState(false);
  const [muralView, setMuralView] = useState<'spiral' | 'watershed' | 'verse'>('spiral');
  const [animFrame, setAnimFrame] = useState(0);
  const [hikmahOpen, setHikmahOpen] = useState<number | null>(null);
  const [caretakerCity, setCaretakerCity] = useState<string>('chicago');
  const [visitedRings, setVisitedRings] = useState<Set<number>>(new Set());
  const animRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Show Iqra epigraph on first open
    const seen = typeof window !== 'undefined' ? localStorage.getItem('haal_mural_iqra_seen') : null;
    if (!seen) {
      setTimeout(() => setShowIqra(true), 400);
    }
    const city = typeof window !== 'undefined' ? (localStorage.getItem('haal_city') || 'chicago') : 'chicago';
    setCaretakerCity(city.toLowerCase());
    // Resource flow animation
    animRef.current = setInterval(() => setAnimFrame(f => (f + 1) % 8), 1200);
    return () => { if (animRef.current) clearInterval(animRef.current); };
  }, []);

  const dismissIqra = () => {
    setShowIqra(false);
    if (typeof window !== 'undefined') localStorage.setItem('haal_mural_iqra_seen', 'true');
  };

  const handleRingClick = (ringIdx: number) => {
    setActiveRing(activeRing === ringIdx ? null : ringIdx);
    setVisitedRings(prev => new Set([...prev, ringIdx]));
    if (ringIdx === 7) setHikmahOpen(7);
  };

  // Determine which rings are "revealed" based on caretaker city
  const cityRingMap: Record<string, number[]> = {
    atlanta: [0, 1], durham: [0, 1], chicago: [2, 3], omaha: [2],
    detroit: [3], 'new orleans': [3, 5], portland: [4, 5], seattle: [4],
    phoenix: [6],
  };
  const cityRevealedRings = cityRingMap[caretakerCity] || [0];
  const isRingRevealed = (ringIdx: number) => ringIdx === 0 || cityRevealedRings.includes(ringIdx) || visitedRings.has(ringIdx);

  const activeRingData = activeRing !== null ? BIOME_RINGS[activeRing] : null;

  return (
    <div style={{ margin: '40px 0' }}>
      {/* ── Iqra Epigraph Overlay ── */}
      {showIqra && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(6,13,18,0.97)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', padding: 32,
        }}>
          <div style={{ maxWidth: 560, textAlign: 'center' }}>
            <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.4)', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 24 }}>
              IQRA · اقرأ · THE FIRST COMMAND
            </div>
            <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(32px,6vw,52px)', color: '#c9a84c', direction: 'rtl', lineHeight: 1.4, marginBottom: 20 }}>
              اقْرَأْ بِاسْمِ رَبِّكَ الَّذِي خَلَقَ
            </div>
            <div style={{ fontSize: 15, color: '#d4c5a9', fontStyle: 'italic', lineHeight: 1.7, marginBottom: 8 }}>
              "Read/recite with the name of your Lord, the One Who created."
            </div>
            <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.4)', marginBottom: 32 }}>
              Quran 96:1 — The first revelation. The first command. The mural is what you are being commanded to read.
            </div>
            <div style={{ fontSize: 12, color: 'rgba(212,197,169,0.5)', lineHeight: 1.7, marginBottom: 40, fontStyle: 'italic' }}>
              Every ring is a surah. Every biome is an ayah. Every fission sequence is a word. Read with His Name.
            </div>
            <button
              onClick={dismissIqra}
              style={{
                background: '#c9a84c', color: '#060d12', border: 'none',
                padding: '14px 40px', fontFamily: 'monospace', fontSize: 11,
                letterSpacing: '.15em', textTransform: 'uppercase', cursor: 'pointer',
              }}
            >
              Enter the Mural →
            </button>
          </div>
        </div>
      )}

      {/* ── Mural Header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.4)', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 8 }}>
          المورال · THE MURAL — Central Hub
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontFamily: 'monospace', fontSize: 'clamp(16px,3vw,22px)', color: '#F5F0E8', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>
              FOREST WATERSHED DESCENT
            </h2>
            <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.5)', fontStyle: 'italic', maxWidth: 500 }}>
              Canopy → Roots mapped to IRL Lens Layers 1–8. Biome rings reveal based on your city and progress.
            </p>
          </div>
          {/* View toggle */}
          <div style={{ display: 'flex', gap: 6 }}>
            {(['spiral', 'watershed', 'verse'] as const).map(v => (
              <button key={v} onClick={() => setMuralView(v)} style={{
                padding: '6px 14px', fontFamily: 'monospace', fontSize: 9,
                letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer',
                background: muralView === v ? 'rgba(201,168,76,0.15)' : 'transparent',
                border: `1px solid ${muralView === v ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.15)'}`,
                color: muralView === v ? '#c9a84c' : 'rgba(212,197,169,0.4)',
              }}>
                {v === 'spiral' ? '◎ Spiral' : v === 'watershed' ? '↓ Watershed' : '✦ 2:164'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── SPIRAL VIEW ── */}
      {muralView === 'spiral' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Left: Concentric rings */}
          <div style={{ position: 'relative', aspectRatio: '1', maxWidth: 420 }}>
            <svg viewBox="0 0 400 400" style={{ width: '100%', height: '100%' }}>
              {BIOME_RINGS.slice().reverse().map((ring, revIdx) => {
                const idx = 7 - revIdx;
                const r = 190 - (idx * 22);
                const revealed = isRingRevealed(idx);
                const isActive = activeRing === idx;
                // Resource flow animation: pulsing dots along ring
                const flowAngle = (animFrame / 8) * 360 + idx * 45;
                const flowX = 200 + r * Math.cos((flowAngle * Math.PI) / 180);
                const flowY = 200 + r * Math.sin((flowAngle * Math.PI) / 180);
                return (
                  <g key={ring.ring}>
                    <circle
                      cx="200" cy="200" r={r}
                      fill={isActive ? `${ring.color}12` : 'transparent'}
                      stroke={revealed ? ring.color : 'rgba(255,255,255,0.06)'}
                      strokeWidth={isActive ? 2 : 1}
                      strokeDasharray={revealed ? 'none' : '4 4'}
                      style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                      onClick={() => handleRingClick(idx)}
                    />
                    {/* Resource flow dot */}
                    {revealed && (
                      <circle cx={flowX} cy={flowY} r={2.5} fill={ring.color} opacity={0.7} />
                    )}
                    {/* Ring label */}
                    {revealed && (
                      <text
                        x={200 + (r - 12) * Math.cos((-30 * Math.PI) / 180)}
                        y={200 + (r - 12) * Math.sin((-30 * Math.PI) / 180)}
                        fill={ring.color}
                        fontSize="7"
                        textAnchor="middle"
                        style={{ pointerEvents: 'none', fontFamily: 'monospace' }}
                      >
                        {ring.biome.split(' ')[0]}
                      </text>
                    )}
                  </g>
                );
              })}
              {/* Center: Forest */}
              <circle cx="200" cy="200" r="28" fill="rgba(45,122,71,0.15)" stroke="#2d7a47" strokeWidth="1.5" style={{ cursor: 'pointer' }} onClick={() => handleRingClick(0)} />
              <text x="200" y="196" fill="#2d7a47" fontSize="7" textAnchor="middle" fontFamily="monospace">FOREST</text>
              <text x="200" y="207" fill="rgba(45,122,71,0.6)" fontSize="6" textAnchor="middle" fontFamily="'Amiri', serif">الغابة</text>
              {/* Iqra label at top */}
              <text x="200" y="18" fill="rgba(201,168,76,0.4)" fontSize="8" textAnchor="middle" fontFamily="monospace" letterSpacing="3">IQRA · اقرأ</text>
            </svg>
          </div>

          {/* Right: Active ring detail */}
          <div>
            {activeRingData ? (
              <div style={{
                background: `${activeRingData.color}08`,
                border: `1px solid ${activeRingData.color}30`,
                borderRadius: 8, padding: '20px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: activeRingData.color }} />
                  <div>
                    <div style={{ fontFamily: 'monospace', fontSize: 10, color: activeRingData.color, letterSpacing: '.1em', textTransform: 'uppercase' }}>
                      Ring {activeRingData.ring} · {activeRingData.biome}
                    </div>
                    <div style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: `${activeRingData.color}80`, direction: 'rtl' }}>
                      {activeRingData.arabic}
                    </div>
                  </div>
                </div>

                {/* 2:164 domain */}
                <div style={{ background: 'rgba(6,13,18,0.5)', borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
                  <div style={{ fontSize: 8, color: 'rgba(201,168,76,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                    2:164 DOMAIN
                  </div>
                  <div style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: '#c9a84c', direction: 'rtl', marginBottom: 4 }}>
                    {activeRingData.domain2164Ar}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', fontStyle: 'italic' }}>
                    "{activeRingData.verse2164Fragment}"
                  </div>
                </div>

                {/* Praise mode */}
                <div style={{ fontSize: 10, color: activeRingData.color, marginBottom: 10 }}>
                  This ring praises through <strong>{activeRingData.praiseMode}</strong>
                </div>

                {/* Names */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 12 }}>
                  {activeRingData.names.map(n => (
                    <span key={n} style={{
                      fontSize: 8, fontFamily: 'monospace', padding: '2px 8px', borderRadius: 20,
                      background: `${activeRingData.color}10`, border: `1px solid ${activeRingData.color}30`,
                      color: activeRingData.color,
                    }}>{n}</span>
                  ))}
                </div>

                {/* Resource flow */}
                <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', marginBottom: 12, fontStyle: 'italic' }}>
                  ↓ {activeRingData.resourceFlow}
                </div>

                {/* Hikmah threshold */}
                <div style={{
                  background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)',
                  borderRadius: 6, padding: '10px 14px', marginBottom: 12,
                }}>
                  <div style={{ fontSize: 8, color: 'rgba(201,168,76,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                    HIKMAH STATION THRESHOLD
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)' }}>{activeRingData.hikmahThreshold}</div>
                </div>

                <button
                  onClick={() => setHikmahOpen(activeRing)}
                  style={{
                    width: '100%', padding: '10px', fontFamily: 'monospace', fontSize: 9,
                    letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer',
                    background: `${activeRingData.color}15`, border: `1px solid ${activeRingData.color}40`,
                    color: activeRingData.color,
                  }}
                >
                  Open Hikmah Station →
                </button>
              </div>
            ) : (
              <div style={{ padding: '32px 20px', textAlign: 'center' }}>
                <div style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: 'rgba(201,168,76,0.3)', marginBottom: 12 }}>
                  اقرأ
                </div>
                <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.3)', lineHeight: 1.7 }}>
                  Select a ring to read it. The mural opens from the nearest — the Forest center. Each ring is a domain from Quran 2:164.
                </p>
                <p style={{ fontSize: 10, color: 'rgba(212,197,169,0.2)', marginTop: 12, fontStyle: 'italic' }}>
                  Rings dimmed = not yet revealed. Visit your city's biome to unlock adjacent rings.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── WATERSHED VIEW — Canopy to Roots = IRL Lens Layers 1–8 ── */}
      {muralView === 'watershed' && (
        <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 8, padding: '24px', overflow: 'hidden' }}>
          <div style={{ fontSize: 9, color: 'rgba(45,122,71,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 16 }}>
            FOREST WATERSHED DESCENT · Canopy → Roots = IRL Lens Layers 1–8
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {BIOME_RINGS[0].irlLayers.map((layer, i) => {
              const depth = i / 7;
              const green = Math.round(122 - depth * 60);
              const layerColor = `rgb(45,${green},${Math.round(71 - depth * 30)})`;
              const isAnimated = animFrame % 8 === i;
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  padding: '12px 16px',
                  background: isAnimated ? `rgba(45,122,71,0.08)` : `rgba(6,13,18,${0.3 + depth * 0.4})`,
                  borderLeft: `3px solid ${layerColor}`,
                  borderBottom: i < 7 ? '1px solid rgba(45,122,71,0.08)' : 'none',
                  transition: 'background 0.4s',
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                    background: `${layerColor}15`, border: `1px solid ${layerColor}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'monospace', fontSize: 10, color: layerColor,
                  }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: layerColor, fontFamily: 'monospace', letterSpacing: '.06em' }}>
                      {layer}
                    </div>
                  </div>
                  {/* Flow indicator */}
                  {i < 7 && (
                    <div style={{ fontSize: 14, color: `${layerColor}40`, flexShrink: 0 }}>↓</div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 6 }}>
            <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>
              THE FOREST IS THE LENS
            </div>
            <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.7 }}>
              "A path through the nearest emerges as a multi-layered forest watershed from highlands to valleys, canopy to roots." The Forest watershed IS the IRL Lens made ecological. When you descend through the Forest layers, you are using the Lens without being told you are using it.
            </p>
          </div>
        </div>
      )}

      {/* ── 2:164 VERSE VIEW — The Mural's Source Code ── */}
      {muralView === 'verse' && (
        <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 8, padding: '24px' }}>
          <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.4)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 16 }}>
            QURAN 2:164 — THE MURAL'S SOURCE CODE
          </div>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(14px,2.5vw,18px)', color: '#c9a84c', direction: 'rtl', lineHeight: 1.8, marginBottom: 16, textAlign: 'right' }}>
            إِنَّ فِي خَلْقِ السَّمَاوَاتِ وَالْأَرْضِ وَاخْتِلَافِ اللَّيْلِ وَالنَّهَارِ وَالْفُلْكِ الَّتِي تَجْرِي فِي الْبَحْرِ بِمَا يَنفَعُ النَّاسَ وَمَا أَنزَلَ اللَّهُ مِنَ السَّمَاءِ مِن مَّاءٍ فَأَحْيَا بِهِ الْأَرْضَ بَعْدَ مَوْتِهَا وَبَثَّ فِيهَا مِن كُلِّ دَابَّةٍ وَتَصْرِيفِ الرِّيَاحِ وَالسَّحَابِ الْمُسَخَّرِ بَيْنَ السَّمَاءِ وَالْأَرْضِ لَآيَاتٍ لِّقَوْمٍ يَعْقِلُونَ
          </div>
          <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.6)', fontStyle: 'italic', lineHeight: 1.7, marginBottom: 24 }}>
            "Indeed, in creation the heavens and the earth, and alternation of the night and the day, and the ships which sail in the sea with what benefits the people and what Allah sent down from the sky of water, giving life thereby the earth after its death, and dispersing therein of every moving creature, and directing the winds and the clouds controlled between the sky and the earth, surely Signs for a people who use their intellect." — (Quran 2:164)
          </p>
          <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.5)', lineHeight: 1.7, marginBottom: 20, fontStyle: 'italic', borderLeft: '3px solid rgba(201,168,76,0.3)', paddingLeft: 16 }}>
            The most comprehensive Ayat-in-creation verse in the Quran. Every domain of the mural is contained in this single verse. This verse IS the mural's source code. The caretaker spiraling outward through the rings is spiraling through the verse itself.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
            {BIOME_RINGS.map(ring => (
              <div key={ring.ring} style={{
                padding: '12px 14px',
                background: `${ring.color}08`, border: `1px solid ${ring.color}20`, borderRadius: 6,
                cursor: 'pointer',
              }} onClick={() => { setMuralView('spiral'); setActiveRing(ring.ring); }}>
                <div style={{ fontSize: 9, color: ring.color, fontFamily: 'monospace', letterSpacing: '.06em', marginBottom: 4 }}>
                  Ring {ring.ring}: {ring.biome}
                </div>
                <div style={{ fontFamily: "'Amiri', serif", fontSize: 11, color: `${ring.color}70`, direction: 'rtl', marginBottom: 4 }}>
                  {ring.domain2164Ar}
                </div>
                <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', fontStyle: 'italic', lineHeight: 1.5 }}>
                  "{ring.verse2164Fragment}"
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── HIKMAH STATION MODAL ── */}
      {hikmahOpen !== null && (
        <EmergenceHikmahStation
          ring={BIOME_RINGS[hikmahOpen]}
          onClose={() => setHikmahOpen(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EMERGENCE-TRIGGERED HIKMAH STATIONS — at all 8 biome ring transitions
// ─────────────────────────────────────────────────────────────────────────────

interface HikmahRing {
  ring: number;
  biome: string;
  arabic: string;
  color: string;
  names: string[];
  hikmahThreshold: string;
  resourceFlow: string;
  domain2164: string;
  verse2164Fragment: string;
  domain2164Ar: string;
  praiseMode: string;
  cities: string[];
  irlLayers: string[];
  sequences: string[];
}

const HIKMAH_QURANIC_VERSES: Record<number, { arabic: string; translation: string; ref: string; hikmah: string }[]> = {
  0: [
    { arabic: 'إِنَّ فِي خَلْقِ السَّمَاوَاتِ وَالْأَرْضِ', translation: 'Indeed, in creation the heavens and the earth', ref: '2:164', hikmah: 'The Forest is the first domain of creation — Al-Khaliq\'s origination made visible in leaf and canopy.' },
    { arabic: 'وَهُوَ الَّذِي أَنزَلَ مِنَ السَّمَاءِ مَاءً', translation: 'And He is the One who sent down from the sky water', ref: '27:60', hikmah: 'Not a tree of theirs was it for you to cause to grow. The growing is His.' },
  ],
  1: [
    { arabic: 'وَاخْتِلَافِ اللَّيْلِ وَالنَّهَارِ', translation: 'And alternation of the night and the day', ref: '2:164', hikmah: 'The Savannah\'s rhythm of dry and wet seasons is the alternation of night and day made ecological.' },
  ],
  2: [
    { arabic: 'وَالْفُلْكِ الَّتِي تَجْرِي فِي الْبَحْرِ', translation: 'And the ships which sail in the sea with what benefits the people', ref: '2:164', hikmah: 'The Grasslands carry provision across the land the way ships carry provision across the sea.' },
    { arabic: 'وَالْأَرْضَ مَدَدْنَاهَا وَأَلْقَيْنَا فِيهَا رَوَاسِيَ', translation: 'And the earth, We have spread it and cast in it firm mountains', ref: '15:19', hikmah: 'The grassland\'s root network is the firm mountain beneath the surface — the hidden infrastructure of stability.' },
  ],
  3: [
    { arabic: 'وَمَا أَنزَلَ اللَّهُ مِنَ السَّمَاءِ مِن مَّاءٍ', translation: 'What Allah sent down from the sky of water, giving life to the earth after its death', ref: '2:164', hikmah: 'The Wetlands are where rain becomes life — the most direct expression of this verse.' },
    { arabic: 'أَلَمْ تَرَ أَنَّ اللَّهَ أَنزَلَ مِنَ السَّمَاءِ مَاءً', translation: 'Do you not see that Allah sends down from the sky water then becomes the earth green?', ref: '22:63', hikmah: 'Al-Latif — the Subtle — works through the wetland\'s invisible filtration.' },
  ],
  4: [
    { arabic: 'وَبَثَّ فِيهَا مِن كُلِّ دَابَّةٍ', translation: 'And dispersing therein of every moving creature', ref: '2:164', hikmah: 'The River Systems carry every moving creature — the salmon, the heron, the otter — in the flow of Ar-Razzaq.' },
  ],
  5: [
    { arabic: 'وَتَصْرِيفِ الرِّيَاحِ', translation: 'And directing the winds', ref: '2:164', hikmah: 'The Coastal zone is where the winds are most visible — Al-Qahhar directing the atmosphere.' },
  ],
  6: [
    { arabic: 'وَالسَّحَابِ الْمُسَخَّرِ بَيْنَ السَّمَاءِ وَالْأَرْضِ', translation: 'And the clouds controlled between the sky and the earth', ref: '2:164', hikmah: 'The Desert waits for the clouds. As-Sabur — patience — is the Desert\'s mode of praise.' },
    { arabic: 'إِنَّا كُلَّ شَيْءٍ خَلَقْنَاهُ بِقَدَرٍ', translation: 'Indeed, We created everything by a measure', ref: '54:49', hikmah: 'The Desert\'s scarcity is not a flaw — it is a measure. Al-Qadir decreed the measure of rain.' },
  ],
  7: [
    { arabic: 'لَآيَاتٍ لِّقَوْمٍ يَعْقِلُونَ', translation: 'Surely Signs for a people who use their intellect', ref: '2:164', hikmah: 'The Quantum Threshold is where the intellect reaches its limit and the heart takes over.' },
    { arabic: 'فَسُبْحَانَ الَّذِي بِيَدِهِ مَلَكُوتُ كُلِّ شَيْءٍ وَإِلَيْهِ تُرْجَعُونَ', translation: 'So glory be to the One in Whose Hand is dominion of all things, and to Him you will be returned', ref: '36:83', hikmah: 'The Uboor — the crossing. The spiral doesn\'t spiral outward infinitely. It curves back. To Him.' },
  ],
};

export function EmergenceHikmahStation({ ring, onClose }: { ring: HikmahRing; onClose: () => void }) {
  const [activeTab, setActiveTab] = useState<'connections' | 'flow' | 'names' | 'verses' | 'purpose'>('connections');
  const verses = HIKMAH_QURANIC_VERSES[ring.ring] || [];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: 'rgba(6,13,18,0.96)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, overflowY: 'auto',
    }}>
      <div style={{
        width: '100%', maxWidth: 680,
        background: '#060d12',
        border: `1px solid ${ring.color}30`,
        borderRadius: 12,
        overflow: 'hidden',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          background: `${ring.color}08`,
          borderBottom: `1px solid ${ring.color}20`,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 8, color: `${ring.color}60`, letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 6 }}>
              محطة الحكمة · HIKMAH STATION — Ring {ring.ring} Transition
            </div>
            <h3 style={{ fontFamily: 'monospace', fontSize: 16, color: '#F5F0E8', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>
              {ring.biome.toUpperCase()}
            </h3>
            <div style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: `${ring.color}70`, direction: 'rtl' }}>
              {ring.arabic}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.4)', marginTop: 6, fontStyle: 'italic' }}>
              {ring.hikmahThreshold}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: '1px solid rgba(255,255,255,0.1)',
            color: 'rgba(212,197,169,0.4)', cursor: 'pointer', padding: '6px 10px',
            fontFamily: 'monospace', fontSize: 12, borderRadius: 4,
          }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${ring.color}15`, overflowX: 'auto' }}>
          {(['connections', 'flow', 'names', 'verses', 'purpose'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '10px 16px', fontFamily: 'monospace', fontSize: 8,
              letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer',
              background: activeTab === tab ? `${ring.color}10` : 'transparent',
              border: 'none', borderBottom: `2px solid ${activeTab === tab ? ring.color : 'transparent'}`,
              color: activeTab === tab ? ring.color : 'rgba(212,197,169,0.3)',
              whiteSpace: 'nowrap',
            }}>
              {tab === 'connections' ? '⬡ Connections' : tab === 'flow' ? '↓ Flow' : tab === 'names' ? '✦ Names' : tab === 'verses' ? '☽ Verses' : '◎ Purpose'}
            </button>
          ))}
        </div>

        <div style={{ padding: '20px 24px' }}>
          {/* CONNECTIONS TAB */}
          {activeTab === 'connections' && (
            <div>
              <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 14 }}>
                GATHERED CONNECTIONS — What flows through this biome
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div style={{ padding: '12px', background: `${ring.color}06`, border: `1px solid ${ring.color}15`, borderRadius: 6 }}>
                  <div style={{ fontSize: 8, color: ring.color, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>2:164 DOMAIN</div>
                  <div style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: '#c9a84c', direction: 'rtl', marginBottom: 4 }}>{ring.domain2164Ar}</div>
                  <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', fontStyle: 'italic' }}>"{ring.verse2164Fragment}"</div>
                </div>
                <div style={{ padding: '12px', background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 6 }}>
                  <div style={{ fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>PRAISE MODE</div>
                  <div style={{ fontSize: 14, color: '#c9a84c', fontFamily: 'monospace', marginBottom: 4 }}>{ring.praiseMode}</div>
                  <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.4)' }}>This ring praises through {ring.praiseMode.toLowerCase()}</div>
                </div>
              </div>
              {ring.sequences.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>IRL LENS SEQUENCES IN THIS RING</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {ring.sequences.map(s => (
                      <Link key={s} href="/inner-reality-lens" style={{
                        fontSize: 9, fontFamily: 'monospace', padding: '4px 10px', borderRadius: 4,
                        background: `${ring.color}10`, border: `1px solid ${ring.color}25`,
                        color: ring.color, textDecoration: 'none',
                      }}>{s} →</Link>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ padding: '12px 16px', background: 'rgba(6,13,18,0.5)', borderRadius: 6, borderLeft: `3px solid ${ring.color}` }}>
                <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', lineHeight: 1.7, fontStyle: 'italic' }}>
                  "A Hikmah Station hosts gathered information highlighting connections, processes, and diagrams unfolding how resources flow throughout habitats and shared spaces — gesturing at purpose."
                </div>
              </div>
            </div>
          )}

          {/* FLOW TAB */}
          {activeTab === 'flow' && (
            <div>
              <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 14 }}>
                RESOURCE FLOW DIAGRAM
              </div>
              <div style={{ marginBottom: 20 }}>
                {ring.resourceFlow.split(' → ').map((step, i, arr) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: i < arr.length - 1 ? 8 : 0 }}>
                    <div style={{
                      padding: '10px 16px', borderRadius: 6, flex: 1,
                      background: `${ring.color}${Math.round(8 + i * 4).toString(16).padStart(2, '0')}`,
                      border: `1px solid ${ring.color}${Math.round(20 + i * 8).toString(16).padStart(2, '0')}`,
                    }}>
                      <div style={{ fontSize: 11, color: ring.color, fontFamily: 'monospace' }}>{step}</div>
                    </div>
                    {i < arr.length - 1 && <div style={{ fontSize: 18, color: `${ring.color}40` }}>↓</div>}
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
                  IRL LENS LAYERS IN THIS BIOME
                </div>
                {ring.irlLayers.map((layer, i) => (
                  <div key={i} style={{
                    padding: '8px 12px', marginBottom: 4,
                    background: 'rgba(6,13,18,0.5)',
                    borderLeft: `2px solid ${ring.color}${Math.round(30 + i * 10).toString(16).padStart(2, '0')}`,
                    fontSize: 11, color: 'rgba(212,197,169,0.6)',
                  }}>
                    {layer}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* NAMES TAB */}
          {activeTab === 'names' && (
            <div>
              <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 14 }}>
                NAMES SYNTHESIS — Active in this biome
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginBottom: 20 }}>
                {ring.names.map(name => (
                  <div key={name} style={{
                    padding: '14px', background: `${ring.color}08`,
                    border: `1px solid ${ring.color}25`, borderRadius: 8,
                  }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, color: ring.color, marginBottom: 4 }}>{name}</div>
                    <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.4)', lineHeight: 1.5 }}>
                      Active in {ring.biome} through {ring.praiseMode.toLowerCase()}
                    </div>
                  </div>
                ))}
              </div>
              <Link href="/names-reference" style={{
                display: 'inline-block', fontSize: 9, fontFamily: 'monospace',
                padding: '8px 16px', border: `1px solid ${ring.color}30`,
                color: ring.color, textDecoration: 'none', letterSpacing: '.1em', textTransform: 'uppercase',
              }}>
                Explore Names Reference →
              </Link>
            </div>
          )}

          {/* VERSES TAB */}
          {activeTab === 'verses' && (
            <div>
              <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 14 }}>
                QURANIC DEPTH LAYERS
              </div>
              {verses.length > 0 ? verses.map((v, i) => (
                <div key={i} style={{
                  marginBottom: 16, padding: '16px',
                  background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)',
                  borderRadius: 8,
                }}>
                  <div style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: '#c9a84c', direction: 'rtl', textAlign: 'right', marginBottom: 8 }}>
                    {v.arabic}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(212,197,169,0.7)', fontStyle: 'italic', marginBottom: 8 }}>
                    "{v.translation}" — ({v.ref})
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.5)', lineHeight: 1.6, borderTop: '1px solid rgba(201,168,76,0.08)', paddingTop: 8 }}>
                    {v.hikmah}
                  </div>
                </div>
              )) : (
                <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.3)', fontStyle: 'italic' }}>
                  Verses for this ring are being gathered. Return after deeper exploration.
                </p>
              )}
            </div>
          )}

          {/* PURPOSE TAB — Gesture at Purpose */}
          {activeTab === 'purpose' && (
            <div>
              <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 14 }}>
                GESTURE AT PURPOSE — What does this recognition call you to?
              </div>
              <div style={{ padding: '16px', background: `${ring.color}06`, border: `1px solid ${ring.color}20`, borderRadius: 8, marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.8, fontStyle: 'italic', marginBottom: 12 }}>
                  "A Hikmah Station gestures at purpose — it doesn't declare purpose. It points. The caretaker follows the connections to their Source."
                </div>
                <div style={{ fontSize: 12, color: ring.color, lineHeight: 1.7 }}>
                  You have recognized {ring.biome} as a domain of {ring.praiseMode.toLowerCase()}. You have seen the Names {ring.names.slice(0, 2).join(' and ')} active here. The verse 2:164 places this domain in the index of creation's praise.
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
                  REFLECTION PROMPTS
                </div>
                {[
                  `What trust (Amanah) does recognizing ${ring.biome} as a domain of ${ring.praiseMode.toLowerCase()} place on you?`,
                  `Where in your ward does this biome's mode of praise appear — and where is it absent?`,
                  `What would it mean to tend this domain Fisabilillah — in the path of Allah?`,
                ].map((prompt, i) => (
                  <div key={i} style={{
                    padding: '10px 14px', marginBottom: 8,
                    background: 'rgba(6,13,18,0.5)', borderLeft: `2px solid ${ring.color}40`,
                    fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.6,
                  }}>
                    {i + 1}. {prompt}
                  </div>
                ))}
              </div>
              <Link href="/muslim-resource-guide" style={{
                display: 'inline-block', fontSize: 9, fontFamily: 'monospace',
                padding: '10px 20px', background: `${ring.color}15`,
                border: `1px solid ${ring.color}40`, color: ring.color,
                textDecoration: 'none', letterSpacing: '.1em', textTransform: 'uppercase',
              }}>
                Bridge to MRG Resources →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FOUR-BEAT IRL LENS QUICK-MODE — Looking → Seeing → Recalling → Recognizing
// The field form for daily practice (vs. the 8-layer expanded form)
// ─────────────────────────────────────────────────────────────────────────────

export function FourBeatLensQuickMode() {
  const [beat, setBeat] = useState<0 | 1 | 2 | 3 | null>(null);
  const [entries, setEntries] = useState({ looking: '', seeing: '', recalling: '', recognizing: '' });
  const [completed, setCompleted] = useState(false);
  const [savedEntries, setSavedEntries] = useState<typeof entries[]>([]);

  const BEATS = [
    {
      id: 0 as const,
      arabic: 'لَمْحَة',
      label: 'Looking',
      sublabel: 'Hawas Activation',
      color: '#2d7a47',
      prompt: 'Raw sensory contact. What do you perceive right now? What is here?',
      description: 'لَمْحَة — Hawas activation. Raw sensory contact. What do you perceive?',
      field: 'looking' as const,
    },
    {
      id: 1 as const,
      arabic: 'رُؤْيَة',
      label: 'Seeing',
      sublabel: 'Zahir Registration',
      color: '#2d8a87',
      prompt: 'The thing is perceived as a thing with form, location, state. What is it? Describe its Zahir.',
      description: 'رُؤْيَة — Zahir registration. The thing is perceived as a thing with form, location, state.',
      field: 'seeing' as const,
    },
    {
      id: 2 as const,
      arabic: 'تَذَكُّر',
      label: 'Recalling',
      sublabel: 'Names Surface',
      color: '#c9a84c',
      prompt: 'The vocabulary of recognition activates. Which Names surface? What do you recall about this?',
      description: 'تَذَكُّر — Names surface. The vocabulary of recognition activates. You recall: this greenness is Al-Musawwir.',
      field: 'recalling' as const,
    },
    {
      id: 3 as const,
      arabic: 'مَعْرِفَة',
      label: 'Recognizing',
      sublabel: 'Haqq Arrives',
      color: '#8a8ab8',
      prompt: 'The thing is known not as itself but as an Ayah — a sign pointing back to its Source. What is the Haqq here?',
      description: 'مَعْرِفَة — Haqq arrives. The thing is known not as itself but as an Ayah pointing back to its Source.',
      field: 'recognizing' as const,
    },
  ];

  const handleSave = () => {
    if (entries.looking || entries.seeing || entries.recalling || entries.recognizing) {
      setSavedEntries(prev => [entries, ...prev.slice(0, 4)]);
      setCompleted(true);
      if (typeof window !== 'undefined') {
        const existing = JSON.parse(localStorage.getItem('haal_four_beat_entries') || '[]');
        localStorage.setItem('haal_four_beat_entries', JSON.stringify([entries, ...existing.slice(0, 9)]));
      }
    }
  };

  const handleReset = () => {
    setEntries({ looking: '', seeing: '', recalling: '', recognizing: '' });
    setBeat(null);
    setCompleted(false);
  };

  return (
    <div style={{ margin: '32px 0' }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.4)', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 6 }}>
          IRL LENS QUICK-MODE · الوضع السريع
        </div>
        <h3 style={{ fontFamily: 'monospace', fontSize: 14, color: '#F5F0E8', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
          FOUR-BEAT FIELD FORM
        </h3>
        <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.5)', lineHeight: 1.7, fontStyle: 'italic' }}>
          "After some time, you're handed a lens, and we go around again, looking — seeing, recalling — recognizing." — Open Letter
        </p>
        <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.4)', marginTop: 6 }}>
          The field form for daily practice. Four breaths per encounter. Walk through the ward: look, see, recall, recognize.
        </p>
      </div>

      {/* Four beat display */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 20 }}>
        {BEATS.map((b, i) => (
          <button
            key={b.id}
            onClick={() => setBeat(beat === b.id ? null : b.id)}
            style={{
              padding: '14px 10px', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
              background: beat === b.id ? `${b.color}15` : 'rgba(6,13,18,0.6)',
              border: `1px solid ${beat === b.id ? b.color + '50' : 'rgba(255,255,255,0.06)'}`,
              transition: 'all 0.2s',
            }}
          >
            <div style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: b.color, marginBottom: 4 }}>{b.arabic}</div>
            <div style={{ fontFamily: 'monospace', fontSize: 9, color: b.color, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 2 }}>{b.label}</div>
            <div style={{ fontSize: 8, color: 'rgba(212,197,169,0.3)' }}>{b.sublabel}</div>
            {entries[b.field] && (
              <div style={{ marginTop: 6, width: 6, height: 6, borderRadius: '50%', background: b.color, margin: '6px auto 0' }} />
            )}
          </button>
        ))}
      </div>

      {/* Active beat input */}
      {beat !== null && !completed && (
        <div style={{
          background: `${BEATS[beat].color}08`,
          border: `1px solid ${BEATS[beat].color}25`,
          borderRadius: 8, padding: '16px 20px', marginBottom: 16,
        }}>
          <div style={{ fontSize: 9, color: BEATS[beat].color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>
            {BEATS[beat].label} — {BEATS[beat].sublabel}
          </div>
          <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.6, marginBottom: 10, fontStyle: 'italic' }}>
            {BEATS[beat].description}
          </p>
          <textarea
            value={entries[BEATS[beat].field]}
            onChange={e => setEntries(prev => ({ ...prev, [BEATS[beat].field]: e.target.value }))}
            placeholder={BEATS[beat].prompt}
            rows={3}
            style={{
              width: '100%', background: 'rgba(6,13,18,0.5)',
              border: `1px solid ${BEATS[beat].color}20`, borderRadius: 6,
              padding: '10px 14px', color: '#d4c5a9', fontSize: 12,
              fontFamily: 'inherit', resize: 'none', outline: 'none', lineHeight: 1.6,
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {beat < 3 && (
              <button onClick={() => setBeat((beat + 1) as 0 | 1 | 2 | 3)} style={{
                padding: '8px 16px', fontFamily: 'monospace', fontSize: 9,
                letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer',
                background: `${BEATS[beat].color}15`, border: `1px solid ${BEATS[beat].color}40`,
                color: BEATS[beat].color,
              }}>
                Next Beat →
              </button>
            )}
            {beat === 3 && (
              <button onClick={handleSave} style={{
                padding: '8px 16px', fontFamily: 'monospace', fontSize: 9,
                letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer',
                background: '#c9a84c', border: 'none', color: '#060d12',
              }}>
                Complete — الحمد لله
              </button>
            )}
          </div>
        </div>
      )}

      {/* Completed state */}
      {completed && (
        <div style={{
          background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: 8, padding: '16px 20px', marginBottom: 16,
        }}>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: '#c9a84c', marginBottom: 8 }}>الحمد لله</div>
          <div style={{ fontSize: 10, color: 'rgba(201,168,76,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 12 }}>
            FOUR-BEAT COMPLETE — We go around again
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            {BEATS.map(b => entries[b.field] ? (
              <div key={b.id} style={{ padding: '8px 12px', background: `${b.color}08`, borderLeft: `2px solid ${b.color}`, borderRadius: '0 4px 4px 0' }}>
                <div style={{ fontSize: 8, color: b.color, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 3 }}>{b.label}</div>
                <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.5 }}>{entries[b.field].slice(0, 60)}{entries[b.field].length > 60 ? '...' : ''}</div>
              </div>
            ) : null)}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleReset} style={{
              padding: '8px 16px', fontFamily: 'monospace', fontSize: 9,
              letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer',
              background: 'transparent', border: '1px solid rgba(201,168,76,0.2)', color: 'rgba(201,168,76,0.5)',
            }}>
              Go Around Again →
            </button>
            <Link href="/inner-reality-lens" style={{
              padding: '8px 16px', fontFamily: 'monospace', fontSize: 9,
              letterSpacing: '.1em', textTransform: 'uppercase',
              background: 'rgba(45,138,135,0.1)', border: '1px solid rgba(45,138,135,0.3)',
              color: '#2d8a87', textDecoration: 'none',
            }}>
              Full 8-Layer Lens →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HANDED A LENS — Presentation moment in Guided Walkthrough
// ─────────────────────────────────────────────────────────────────────────────

export function HandedALensMoment({ onReceive }: { onReceive?: () => void }) {
  const [received, setReceived] = useState(false);
  const [animStep, setAnimStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setAnimStep(s => (s + 1) % 4), 1800);
    return () => clearInterval(interval);
  }, []);

  const handleReceive = () => {
    setReceived(true);
    if (typeof window !== 'undefined') localStorage.setItem('haal_lens_received', 'true');
    if (onReceive) onReceive();
  };

  if (received) {
    return (
      <div style={{
        background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.25)',
        borderRadius: 8, padding: '20px 24px',
      }}>
        <div style={{ fontSize: 9, color: '#2d8a87', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
          THE LENS IS IN YOUR HANDS
        </div>
        <p style={{ fontSize: 13, color: '#d4c5a9', lineHeight: 1.7, marginBottom: 12 }}>
          You have been handed the lens. Not earned. Not unlocked. Handed — like a tool given by a master to an apprentice. Now look again.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link href="/inner-reality-lens" style={{
            fontSize: 9, fontFamily: 'monospace', padding: '8px 16px',
            background: '#2d8a87', color: '#060d12', textDecoration: 'none',
            letterSpacing: '.1em', textTransform: 'uppercase',
          }}>
            Use the Lens →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'rgba(6,13,18,0.9)', border: '1px solid rgba(45,138,135,0.2)',
      borderRadius: 12, padding: '32px 28px', textAlign: 'center',
    }}>
      {/* Animated lens visual */}
      <div style={{ marginBottom: 24, position: 'relative', display: 'inline-block' }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%',
          border: `2px solid rgba(45,138,135,${0.3 + animStep * 0.15})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto',
          boxShadow: `0 0 ${20 + animStep * 8}px rgba(45,138,135,0.15)`,
          transition: 'all 0.6s ease',
        }}>
          <div style={{
            width: 50, height: 50, borderRadius: '50%',
            border: '1px solid rgba(45,138,135,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ fontSize: 20, color: '#2d8a87' }}>◎</div>
          </div>
        </div>
      </div>

      <div style={{ fontSize: 9, color: 'rgba(45,138,135,0.5)', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 16 }}>
        AFTER SOME TIME · بعد مرور الوقت
      </div>
      <h3 style={{ fontFamily: 'monospace', fontSize: 18, color: '#F5F0E8', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 12 }}>
        HERE. TAKE THIS.
      </h3>
      <p style={{ fontSize: 14, color: '#d4c5a9', lineHeight: 1.8, maxWidth: 440, margin: '0 auto 8px', fontStyle: 'italic' }}>
        "After some time, you're handed a lens, and we go around again, looking — seeing, recalling — recognizing."
      </p>
      <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.4)', marginBottom: 24, lineHeight: 1.6 }}>
        The lens is not earned through progression. It is handed to you. Like a provision. A mata. Now look again — the same territory, with new eyes.
      </p>
      <button
        onClick={handleReceive}
        style={{
          padding: '14px 32px', fontFamily: 'monospace', fontSize: 11,
          letterSpacing: '.15em', textTransform: 'uppercase', cursor: 'pointer',
          background: '#2d8a87', border: 'none', color: '#060d12',
        }}
      >
        Receive the Lens →
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GARMENT WEAVING-IN-PROGRESS — Shows strand caught, stretched, threaded
// ─────────────────────────────────────────────────────────────────────────────

export function GarmentWeavingProcess({ observationText = '', namesExtracted = [], onComplete }: {
  observationText?: string;
  namesExtracted?: string[];
  onComplete?: () => void;
}) {
  const [weavingStep, setWeavingStep] = useState<'idle' | 'catching' | 'stretching' | 'threading' | 'woven'>('idle');
  const [animProgress, setAnimProgress] = useState(0);

  const startWeaving = () => {
    setWeavingStep('catching');
    setAnimProgress(0);
    const steps: Array<typeof weavingStep> = ['catching', 'stretching', 'threading', 'woven'];
    let i = 0;
    const interval = setInterval(() => {
      i++;
      if (i < steps.length) {
        setWeavingStep(steps[i]);
        setAnimProgress(i * 33);
      } else {
        clearInterval(interval);
        setAnimProgress(100);
        if (onComplete) onComplete();
      }
    }, 1200);
  };

  const WEAVING_STEPS = [
    {
      id: 'catching',
      label: 'Strand Caught',
      arabic: 'الخيط ممسوك',
      description: 'A fleeting thought, like a strand caught between mental fingertips.',
      color: '#2d7a47',
      icon: '◎',
    },
    {
      id: 'stretching',
      label: 'Stretched Out',
      arabic: 'ممتد للفحص',
      description: 'Stretched out to be examined — the 8 layers being processed.',
      color: '#2d8a87',
      icon: '⬡',
    },
    {
      id: 'threading',
      label: 'Threaded',
      arabic: 'مُنسَّق',
      description: 'Threaded into an expression — connecting to Names, to bridge links, to community catalog.',
      color: '#c9a84c',
      icon: '✦',
    },
    {
      id: 'woven',
      label: 'Woven',
      arabic: 'منسوج',
      description: 'The thread is added to the Garment. The caretaker participated in the weaving.',
      color: '#8a8ab8',
      icon: '◈',
    },
  ];

  return (
    <div style={{ margin: '24px 0' }}>
      <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.4)', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 10 }}>
        رَشَطَ التَّقْوَى · RESHAT AL-TAQWAH — Weaving in Progress
      </div>

      {weavingStep === 'idle' ? (
        <div style={{
          background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(138,138,184,0.2)',
          borderRadius: 8, padding: '20px', textAlign: 'center',
        }}>
          <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.5)', lineHeight: 1.7, marginBottom: 16, fontStyle: 'italic' }}>
            "A fleeting thought, like a strand caught between mental fingertips, stretched out to be examined, threaded into an expression."
          </p>
          <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.4)', marginBottom: 20 }}>
            When you submit an observation, watch the strand being caught, stretched, and threaded into your Garment.
          </p>
          <button onClick={startWeaving} style={{
            padding: '10px 24px', fontFamily: 'monospace', fontSize: 9,
            letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer',
            background: 'rgba(138,138,184,0.1)', border: '1px solid rgba(138,138,184,0.3)',
            color: '#8a8ab8',
          }}>
            Begin Weaving →
          </button>
        </div>
      ) : (
        <div style={{ background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(138,138,184,0.2)', borderRadius: 8, overflow: 'hidden' }}>
          {/* Progress bar */}
          <div style={{ height: 3, background: 'rgba(138,138,184,0.1)' }}>
            <div style={{
              height: '100%', background: 'linear-gradient(to right, #2d7a47, #2d8a87, #c9a84c, #8a8ab8)',
              width: `${animProgress}%`, transition: 'width 0.8s ease',
            }} />
          </div>

          <div style={{ padding: '20px' }}>
            {/* Steps */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 20 }}>
              {WEAVING_STEPS.map((step, i) => {
                const stepOrder = ['catching', 'stretching', 'threading', 'woven'];
                const currentIdx = stepOrder.indexOf(weavingStep);
                const stepIdx = stepOrder.indexOf(step.id);
                const isActive = weavingStep === step.id;
                const isDone = stepIdx < currentIdx || weavingStep === 'woven';
                return (
                  <div key={step.id} style={{
                    padding: '10px 8px', textAlign: 'center', borderRadius: 6,
                    background: isActive ? `${step.color}15` : isDone ? `${step.color}08` : 'rgba(6,13,18,0.5)',
                    border: `1px solid ${isActive ? step.color + '40' : isDone ? step.color + '20' : 'rgba(255,255,255,0.05)'}`,
                    transition: 'all 0.4s',
                  }}>
                    <div style={{ fontSize: 16, color: isActive ? step.color : isDone ? `${step.color}60` : 'rgba(255,255,255,0.1)', marginBottom: 4 }}>
                      {isDone ? '✓' : step.icon}
                    </div>
                    <div style={{ fontSize: 8, fontFamily: 'monospace', color: isActive ? step.color : isDone ? `${step.color}60` : 'rgba(212,197,169,0.2)', letterSpacing: '.04em' }}>
                      {step.label}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Active step description */}
            {weavingStep !== 'idle' && (
              <div style={{
                padding: '12px 16px',
                background: `${WEAVING_STEPS.find(s => s.id === weavingStep)?.color || '#c9a84c'}08`,
                borderLeft: `3px solid ${WEAVING_STEPS.find(s => s.id === weavingStep)?.color || '#c9a84c'}`,
                borderRadius: '0 6px 6px 0',
                marginBottom: weavingStep === 'woven' ? 16 : 0,
              }}>
                <div style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: WEAVING_STEPS.find(s => s.id === weavingStep)?.color, direction: 'rtl', marginBottom: 4 }}>
                  {WEAVING_STEPS.find(s => s.id === weavingStep)?.arabic}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(212,197,169,0.7)', lineHeight: 1.6, fontStyle: 'italic' }}>
                  {WEAVING_STEPS.find(s => s.id === weavingStep)?.description}
                </div>
              </div>
            )}

            {weavingStep === 'woven' && (
              <div style={{ textAlign: 'center', paddingTop: 8 }}>
                <div style={{ fontFamily: "'Amiri', serif", fontSize: 24, color: '#c9a84c', marginBottom: 6 }}>الحمد لله</div>
                <div style={{ fontSize: 10, color: 'rgba(201,168,76,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 12 }}>
                  THE GARMENT IS YOUR HANDIWORK — NOT THE PLATFORM'S
                </div>
                {namesExtracted.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 12 }}>
                    {namesExtracted.map(n => (
                      <span key={n} style={{
                        fontSize: 9, fontFamily: 'monospace', padding: '3px 10px', borderRadius: 20,
                        background: 'rgba(138,138,184,0.1)', border: '1px solid rgba(138,138,184,0.3)',
                        color: '#8a8ab8',
                      }}>{n}</span>
                    ))}
                  </div>
                )}
                <button onClick={() => { setWeavingStep('idle'); setAnimProgress(0); }} style={{
                  padding: '8px 20px', fontFamily: 'monospace', fontSize: 9,
                  letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer',
                  background: 'transparent', border: '1px solid rgba(138,138,184,0.2)', color: 'rgba(138,138,184,0.5)',
                }}>
                  Weave Another Strand →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RECURSIVE REVISITATION DEPTH — Ward/biome tiles deepen on return visits
// First visit: Zahir. Second: Sunnat Allah. Third: Batin.
// ─────────────────────────────────────────────────────────────────────────────

const REVISIT_LAYERS = [
  {
    visit: 1,
    layer: 'Zahir',
    arabic: 'الظاهر',
    color: '#2d7a47',
    description: 'First encounter — the apparent surface. What is visible, observable, present.',
    prompt: 'What do you see? Describe the Zahir of this place.',
  },
  {
    visit: 2,
    layer: 'Sunnat Allah',
    arabic: 'سُنَّة اللَّه',
    color: '#2d8a87',
    description: 'Second visit — the pattern of Allah. What recurring processes, rhythms, and laws are at work here?',
    prompt: 'What patterns do you recognize? What Sunnat Allah is operating in this place?',
  },
  {
    visit: 3,
    layer: 'Batin',
    arabic: 'الباطن',
    color: '#8a8ab8',
    description: 'Third visit — the hidden depth. What is beneath the surface? What Names are active in the unseen dimensions?',
    prompt: 'What is hidden here? What do you now see that you could not see before?',
  },
];

export function RecursiveRevisitationSystem({ wardName = 'Your Ward', biome = 'Forest' }: { wardName?: string; biome?: string }) {
  const [visitCount, setVisitCount] = useState(1);
  const [reflections, setReflections] = useState<Record<number, string>>({});
  const [activeVisit, setActiveVisit] = useState(1);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const key = `haal_ward_visits_${wardName.toLowerCase().replace(/\s/g, '_')}`;
      const stored = parseInt(localStorage.getItem(key) || '1', 10);
      setVisitCount(stored);
      setActiveVisit(Math.min(stored, 3));
    }
  }, [wardName]);

  const simulateReturn = () => {
    const newCount = Math.min(visitCount + 1, 3);
    setVisitCount(newCount);
    setActiveVisit(newCount);
    if (typeof window !== 'undefined') {
      const key = `haal_ward_visits_${wardName.toLowerCase().replace(/\s/g, '_')}`;
      localStorage.setItem(key, String(newCount));
    }
  };

  const currentLayer = REVISIT_LAYERS[activeVisit - 1];

  return (
    <div style={{ margin: '24px 0' }}>
      <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.4)', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 10 }}>
        RECURSIVE REVISITATION — "We go around again"
      </div>

      {/* Visit counter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {REVISIT_LAYERS.map((layer, i) => (
          <button
            key={layer.visit}
            onClick={() => i < visitCount && setActiveVisit(layer.visit)}
            style={{
              flex: 1, padding: '10px 8px', borderRadius: 6, cursor: i < visitCount ? 'pointer' : 'default',
              background: activeVisit === layer.visit ? `${layer.color}15` : i < visitCount ? `${layer.color}06` : 'rgba(6,13,18,0.4)',
              border: `1px solid ${activeVisit === layer.visit ? layer.color + '40' : i < visitCount ? layer.color + '15' : 'rgba(255,255,255,0.04)'}`,
              textAlign: 'center',
            }}
          >
            <div style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: i < visitCount ? layer.color : 'rgba(255,255,255,0.1)', marginBottom: 3 }}>
              {layer.arabic}
            </div>
            <div style={{ fontSize: 8, fontFamily: 'monospace', color: i < visitCount ? layer.color : 'rgba(255,255,255,0.1)', letterSpacing: '.04em' }}>
              Visit {layer.visit}: {layer.layer}
            </div>
            {i >= visitCount && <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.1)', marginTop: 2 }}>◎ locked</div>}
          </button>
        ))}
      </div>

      {/* Active layer */}
      <div style={{
        background: `${currentLayer.color}08`,
        border: `1px solid ${currentLayer.color}25`,
        borderRadius: 8, padding: '16px 20px', marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: currentLayer.color }}>{currentLayer.arabic}</div>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 10, color: currentLayer.color, letterSpacing: '.08em', textTransform: 'uppercase' }}>
              Visit {currentLayer.visit}: {currentLayer.layer}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.4)' }}>{wardName} · {biome}</div>
          </div>
        </div>
        <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.6)', lineHeight: 1.7, marginBottom: 12, fontStyle: 'italic' }}>
          {currentLayer.description}
        </p>
        <textarea
          value={reflections[activeVisit] || ''}
          onChange={e => setReflections(prev => ({ ...prev, [activeVisit]: e.target.value }))}
          placeholder={currentLayer.prompt}
          rows={3}
          style={{
            width: '100%', background: 'rgba(6,13,18,0.5)',
            border: `1px solid ${currentLayer.color}15`, borderRadius: 6,
            padding: '10px 14px', color: '#d4c5a9', fontSize: 12,
            fontFamily: 'inherit', resize: 'none', outline: 'none', lineHeight: 1.6,
            boxSizing: 'border-box',
          }}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <p style={{ fontSize: 10, color: 'rgba(212,197,169,0.3)', flex: 1, fontStyle: 'italic' }}>
          "The garden is the same. The caretaker has changed. We go around again."
        </p>
        {visitCount < 3 && (
          <button onClick={simulateReturn} style={{
            padding: '8px 16px', fontFamily: 'monospace', fontSize: 9,
            letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer',
            background: 'transparent', border: `1px solid ${REVISIT_LAYERS[visitCount].color}30`,
            color: REVISIT_LAYERS[visitCount].color, flexShrink: 0,
          }}>
            Return Visit →
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// UBOOR RETURN THRESHOLD — 36:83 as the crossing-over moment after Amanah
// ─────────────────────────────────────────────────────────────────────────────

export function UboorReturnThreshold() {
  const [crossed, setCrossed] = useState(false);
  const [showReflection, setShowReflection] = useState(false);

  return (
    <div style={{ margin: '32px 0' }}>
      <div style={{ fontSize: 9, color: 'rgba(138,138,184,0.4)', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 10 }}>
        عُبُور · UBOOR — The Return Threshold
      </div>

      <div style={{
        background: 'rgba(6,13,18,0.9)',
        border: '1px solid rgba(138,138,184,0.2)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        {/* 36:82-83 */}
        <div style={{ padding: '24px', borderBottom: '1px solid rgba(138,138,184,0.1)' }}>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(14px,2.5vw,18px)', color: '#8a8ab8', direction: 'rtl', lineHeight: 1.8, marginBottom: 12, textAlign: 'right' }}>
            إِنَّمَا أَمْرُهُ إِذَا أَرَادَ شَيْئًا أَن يَقُولَ لَهُ كُن فَيَكُونُ ۝ فَسُبْحَانَ الَّذِي بِيَدِهِ مَلَكُوتُ كُلِّ شَيْءٍ وَإِلَيْهِ تُرْجَعُونَ
          </div>
          <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.6)', fontStyle: 'italic', lineHeight: 1.7, marginBottom: 8 }}>
            "Only His Command when He intends a thing that He says to it, 'Be,' and it is. So glory be to the One in Whose Hand is dominion of all things, and to Him you will be returned." — (Quran 36:82-83)
          </p>
          <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.4)', lineHeight: 1.6, fontStyle: 'italic' }}>
            Kun fayakun — paired with the return. The section began with the mural (the overview of all creation) and closes with the reminder that all of it returns to its Source. The spiral doesn't spiral outward infinitely. It curves back.
          </p>
        </div>

        {/* The crossing arc */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(138,138,184,0.1)' }}>
          <div style={{ fontSize: 9, color: 'rgba(138,138,184,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 12 }}>
            THE RECOGNITION ARC → UBOOR
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {['Mata', 'Encounter', 'Examination', 'Recognition', 'Re-Assembly', 'Amanah', 'Uboor'].map((stage, i, arr) => (
              <React.Fragment key={stage}>
                <div style={{
                  padding: '6px 12px', borderRadius: 4,
                  background: stage === 'Uboor' ? 'rgba(138,138,184,0.15)' : 'rgba(6,13,18,0.5)',
                  border: `1px solid ${stage === 'Uboor' ? 'rgba(138,138,184,0.4)' : 'rgba(255,255,255,0.06)'}`,
                  fontSize: 9, fontFamily: 'monospace',
                  color: stage === 'Uboor' ? '#8a8ab8' : 'rgba(212,197,169,0.4)',
                  letterSpacing: '.04em',
                }}>
                  {stage}
                </div>
                {i < arr.length - 1 && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.1)' }}>→</div>}
              </React.Fragment>
            ))}
          </div>
          <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.4)', marginTop: 12, lineHeight: 1.6, fontStyle: 'italic' }}>
            The recognition arc doesn't terminate in Amanah — it terminates in return to Allah. The Amanah is the final action before the return. Every complete recognition arc is a micro-return. The caretaker says Alhamdulillah. That Alhamdulillah is a return.
          </p>
        </div>

        {/* Crossing moment */}
        <div style={{ padding: '20px 24px' }}>
          {!crossed ? (
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.5)', lineHeight: 1.7, marginBottom: 20, fontStyle: 'italic' }}>
                "The spiral's eighth ring is not the last biome — it's the threshold of return. The threshold is not the unseen as mystery — it's the unseen as destination."
              </p>
              <button
                onClick={() => { setCrossed(true); setShowReflection(true); }}
                style={{
                  padding: '14px 32px', fontFamily: 'monospace', fontSize: 11,
                  letterSpacing: '.15em', textTransform: 'uppercase', cursor: 'pointer',
                  background: 'rgba(138,138,184,0.1)', border: '1px solid rgba(138,138,184,0.4)',
                  color: '#8a8ab8',
                }}
              >
                Cross the Threshold — وَإِلَيْهِ تُرْجَعُونَ
              </button>
            </div>
          ) : (
            <div>
              <div style={{ fontFamily: "'Amiri', serif", fontSize: 28, color: '#8a8ab8', textAlign: 'center', marginBottom: 8 }}>
                وَإِلَيْهِ تُرْجَعُونَ
              </div>
              <div style={{ fontSize: 10, color: 'rgba(138,138,184,0.5)', textAlign: 'center', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 16 }}>
                AND TO HIM YOU WILL BE RETURNED
              </div>
              <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.6)', lineHeight: 1.7, textAlign: 'center', marginBottom: 20, fontStyle: 'italic' }}>
                You have crossed the threshold. The garden becomes transparent. You look at your garden and see through it to the creation it was always pointing toward.
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <Link href="/inner-reality-lens" style={{
                  fontSize: 9, fontFamily: 'monospace', padding: '10px 20px',
                  background: 'rgba(138,138,184,0.1)', border: '1px solid rgba(138,138,184,0.3)',
                  color: '#8a8ab8', textDecoration: 'none', letterSpacing: '.1em', textTransform: 'uppercase',
                }}>
                  Return to the Lens →
                </Link>
                <Link href="/open-letter" style={{
                  fontSize: 9, fontFamily: 'monospace', padding: '10px 20px',
                  background: 'transparent', border: '1px solid rgba(201,168,76,0.2)',
                  color: 'rgba(201,168,76,0.5)', textDecoration: 'none', letterSpacing: '.1em', textTransform: 'uppercase',
                }}>
                  Return to the Letter →
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OBSERVATION CHAIN TAPESTRY — Archive rendered as tapestry showing connections
// "Woven into conversation, brought through spaces that seem to flow together"
// ─────────────────────────────────────────────────────────────────────────────

interface TapestryNode {
  id: string;
  title: string;
  name: string;
  city: string;
  color: string;
  x: number;
  y: number;
  connections: string[];
}

const TAPESTRY_NODES: TapestryNode[] = [
  { id: 'leaf', title: 'Leaf Sequence', name: 'Al-Khaliq', city: 'Chicago', color: '#2d7a47', x: 20, y: 30, connections: ['mycorrhizal', 'canopy'] },
  { id: 'mizan', title: 'Mizan in Streets', name: 'Al-Adl', city: 'Atlanta', color: '#c05050', x: 45, y: 15, connections: ['reentry', 'urban'] },
  { id: 'mycorrhizal', title: 'Mycorrhizal Network', name: 'Al-Latif', city: 'Portland', color: '#2d8a87', x: 30, y: 55, connections: ['leaf', 'water'] },
  { id: 'fisabilillah', title: '40 Days Fisabilillah', name: 'Al-Wadud', city: 'Detroit', color: '#c9a84c', x: 65, y: 40, connections: ['reentry', 'mizan'] },
  { id: 'reentry', title: 'Reentry as Islah', name: 'Al-Tawwab', city: 'Atlanta', color: '#b47828', x: 55, y: 65, connections: ['mizan', 'fisabilillah'] },
  { id: 'urban', title: 'Urban Ayat', name: 'Al-Zahir', city: 'Chicago', color: '#8a8ab8', x: 75, y: 20, connections: ['mizan', 'canopy'] },
  { id: 'water', title: 'Water Cycle Tawbah', name: 'Al-Ghaffar', city: 'Portland', color: '#4a8ab8', x: 15, y: 75, connections: ['mycorrhizal', 'leaf'] },
  { id: 'canopy', title: 'Canopy Photosynthesis', name: 'Ar-Razzaq', city: 'Durham', color: '#2d7a47', x: 80, y: 70, connections: ['leaf', 'urban'] },
];

export function ObservationChainTapestry() {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  const selectedData = selectedNode ? TAPESTRY_NODES.find(n => n.id === selectedNode) : null;
  const connectedIds = selectedData ? new Set(selectedData.connections) : new Set<string>();

  return (
    <div style={{ margin: '32px 0' }}>
      <div style={{ fontSize: 9, color: 'rgba(45,138,135,0.4)', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 10 }}>
        أرشيف سلاسل الملاحظة · OBSERVATION CHAIN TAPESTRY
      </div>
      <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.4)', lineHeight: 1.6, marginBottom: 16, fontStyle: 'italic' }}>
        "A mental note, woven into conversation, brought through spaces that seem to flow together." — The Archive as tapestry, not database.
      </p>

      <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,138,135,0.15)', borderRadius: 8, overflow: 'hidden' }}>
        {/* SVG Tapestry */}
        <div style={{ position: 'relative', width: '100%', paddingBottom: '50%', background: 'rgba(6,13,18,0.9)' }}>
          <svg
            viewBox="0 0 100 50"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Connection lines */}
            {TAPESTRY_NODES.map(node =>
              node.connections.map(connId => {
                const conn = TAPESTRY_NODES.find(n => n.id === connId);
                if (!conn) return null;
                const isHighlighted = selectedNode && (selectedNode === node.id || selectedNode === connId);
                const isHovered = hoveredNode && (hoveredNode === node.id || hoveredNode === connId);
                return (
                  <line
                    key={`${node.id}-${connId}`}
                    x1={node.x} y1={node.y}
                    x2={conn.x} y2={conn.y}
                    stroke={isHighlighted ? node.color : isHovered ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.06)'}
                    strokeWidth={isHighlighted ? 0.4 : 0.2}
                    strokeDasharray={isHighlighted ? 'none' : '1 1'}
                    style={{ transition: 'all 0.3s' }}
                  />
                );
              })
            )}

            {/* Nodes */}
            {TAPESTRY_NODES.map(node => {
              const isSelected = selectedNode === node.id;
              const isConnected = connectedIds.has(node.id);
              const isHovered = hoveredNode === node.id;
              return (
                <g key={node.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedNode(selectedNode === node.id ? null : node.id)}
                  onMouseEnter={() => setHoveredNode(node.id)}
                  onMouseLeave={() => setHoveredNode(null)}
                >
                  <circle
                    cx={node.x} cy={node.y} r={isSelected ? 3.5 : isConnected ? 3 : 2.5}
                    fill={isSelected ? node.color : isConnected ? `${node.color}80` : `${node.color}40`}
                    stroke={isSelected ? node.color : isHovered ? node.color : 'transparent'}
                    strokeWidth={0.5}
                    style={{ transition: 'all 0.2s' }}
                  />
                  <text
                    x={node.x} y={node.y - 3.5}
                    fill={isSelected ? node.color : isConnected ? `${node.color}80` : 'rgba(212,197,169,0.3)'}
                    fontSize="2.5" textAnchor="middle"
                    style={{ pointerEvents: 'none', fontFamily: 'monospace', transition: 'all 0.2s' }}
                  >
                    {node.name}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Selected node detail */}
        {selectedData && (
          <div style={{
            padding: '16px 20px',
            background: `${selectedData.color}08`,
            borderTop: `1px solid ${selectedData.color}20`,
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
              <div>
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: selectedData.color, letterSpacing: '.06em', marginBottom: 2 }}>
                  {selectedData.title}
                </div>
                <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.4)', fontFamily: 'monospace' }}>
                  {selectedData.city} · {selectedData.name}
                </div>
              </div>
              <button onClick={() => setSelectedNode(null)} style={{
                background: 'none', border: 'none', color: 'rgba(212,197,169,0.3)',
                cursor: 'pointer', fontSize: 14, padding: '0 4px',
              }}>✕</button>
            </div>
            <div style={{ fontSize: 9, color: 'rgba(212,197,169,0.3)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
              CONNECTED CHAINS
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {selectedData.connections.map(connId => {
                const conn = TAPESTRY_NODES.find(n => n.id === connId);
                if (!conn) return null;
                return (
                  <button key={connId} onClick={() => setSelectedNode(connId)} style={{
                    fontSize: 9, fontFamily: 'monospace', padding: '3px 10px', borderRadius: 20,
                    background: `${conn.color}10`, border: `1px solid ${conn.color}25`,
                    color: conn.color, cursor: 'pointer',
                  }}>
                    {conn.title} ({conn.name})
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!selectedData && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
            <p style={{ fontSize: 10, color: 'rgba(212,197,169,0.3)', fontStyle: 'italic' }}>
              Select a node to see its connections. The spaces flow together — this observation connects to that one, this Name appears in both.
            </p>
          </div>
        )}
      </div>

      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <Link href="/observation-chain-archive" style={{
          fontSize: 9, fontFamily: 'monospace', color: 'rgba(45,138,135,0.5)',
          textDecoration: 'none', letterSpacing: '.1em', textTransform: 'uppercase',
        }}>
          Full Archive →
        </Link>
      </div>
    </div>
  );
}
